import { useState, useEffect } from 'react';
import {
    Shield,
    FileText,
    X,
    ChevronRight,
    ClipboardList,
    Newspaper
} from 'lucide-react';
import PNBulletinForm from './PNBulletinForm';
import { localApi, API_BASE_URL } from '../utils/localApi';

interface SecuDashboardProps {
    onClose: () => void;
    driverName: string;
    allowedZones: string;
}

type TabType = 'ralentiss' | 'docs' | 'planning' | 'bulletin';

export default function SecuDashboard({ onClose, driverName, allowedZones }: SecuDashboardProps) {
    const [activeTab, setActiveTab] = useState<TabType>('bulletin');
    const [signedBulletins, setSignedBulletins] = useState<any[]>([]);
    const [showPNForm, setShowPNForm] = useState(false);

    useEffect(() => {
        fetchSignedBulletins();
    }, []);

    const fetchSignedBulletins = async () => {
        try {
            const data = await localApi.getDriverBulletins();
            setSignedBulletins(data);
        } catch (err) {
            console.error("Erreur chargement bulletins signés:", err);
        }
    };

    return (
        <div className="fixed inset-0 z-[5000] bg-[#050914] flex flex-col lg:flex-row animate-in fade-in duration-300 font-sans">
            {/* Navigation (Sidebar on Desktop, Bottom Bar on Mobile) */}
            <aside className="w-full lg:w-20 bg-[#0a0f1d] border-t lg:border-t-0 lg:border-r border-white/5 flex lg:flex-col items-center justify-around lg:justify-start py-2 lg:py-6 shrink-0 gap-2 lg:gap-6 order-2 lg:order-1 safe-area-bottom lg:pb-6">
                <div className="hidden lg:flex w-12 h-12 bg-blue-600 rounded-2xl items-center justify-center shadow-lg shadow-blue-900/40 mb-2">
                    <Shield className="text-white w-7 h-7" />
                </div>

                <nav className="flex lg:flex-1 flex-row lg:flex-col gap-2 lg:gap-4 w-full lg:w-auto justify-around lg:justify-start">
                    <button
                        onClick={() => setActiveTab('bulletin')}
                        className={`flex-1 lg:flex-none w-auto lg:w-12 h-14 lg:h-12 rounded-xl flex flex-col items-center justify-center transition-all gap-0.5 lg:gap-1 relative ${activeTab === 'bulletin' ? 'bg-blue-600 text-white' : 'text-slate-500 hover:text-slate-300 hover:bg-white/5'}`}
                    >
                        <Newspaper size={20} />
                        <span className="text-[7px] font-black uppercase tracking-tighter">Bulletin</span>
                    </button>
                    <button
                        onClick={() => setActiveTab('docs')}
                        className={`flex-1 lg:flex-none w-auto lg:w-12 h-14 lg:h-12 rounded-xl flex flex-col items-center justify-center transition-all gap-0.5 lg:gap-1 relative ${activeTab === 'docs' ? 'bg-blue-600 text-white' : 'text-slate-500 hover:text-slate-300 hover:bg-white/5'}`}
                    >
                        <FileText size={20} />
                        <span className="text-[7px] font-black uppercase tracking-tighter">Docs Secu</span>
                    </button>
                    <button
                        onClick={() => setActiveTab('planning')}
                        className={`flex-1 lg:flex-none w-auto lg:w-12 h-14 lg:h-12 rounded-xl flex flex-col items-center justify-center transition-all gap-0.5 lg:gap-1 relative ${activeTab === 'planning' ? 'bg-blue-600 text-white' : 'text-slate-500 hover:text-slate-300 hover:bg-white/5'}`}
                    >
                        <ClipboardList size={20} />
                        <span className="text-[7px] font-black uppercase tracking-tighter">Planning</span>
                    </button>
                </nav>

                <button onClick={onClose} className="hidden lg:flex w-12 h-12 bg-white/5 rounded-xl items-center justify-center text-slate-400 hover:text-white hover:bg-red-500/20 transition-all">
                    <X size={24} />
                </button>
            </aside>

            {/* Main Content Area */}
            <main className="flex-1 flex flex-col min-w-0 overflow-hidden order-1 lg:order-2">
                {/* Header Section */}
                <header className="px-5 lg:px-8 pt-6 lg:pt-8 pb-4 shrink-0 flex justify-between items-start safe-area-top">
                    <div>
                        <h1 className="text-xl lg:text-3xl font-black text-white italic tracking-tight flex items-center gap-3">
                            Mission Support <span className="text-blue-500 text-xs lg:text-sm font-black uppercase not-italic tracking-widest">(SECU)</span>
                        </h1>
                        <p className="text-[8px] lg:text-[10px] text-slate-500 font-bold uppercase tracking-[0.2em] mt-1">Connecté : Agent {driverName}</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="hidden sm:flex px-3 py-1 bg-blue-500/10 border border-blue-500/30 rounded-full text-[10px] font-black text-blue-400 uppercase tracking-widest">Connecté</div>
                        <button onClick={onClose} className="lg:hidden p-2 bg-white/5 rounded-xl text-slate-400">
                            <X size={20} />
                        </button>
                    </div>
                </header>

                <div className="flex-1 overflow-hidden flex flex-col px-4 lg:px-8 pb-4 lg:pb-8">
                    {activeTab === 'bulletin' && (
                        <div className="flex-1 overflow-y-auto space-y-6 animate-in slide-in-from-right-4 duration-500">
                            <div>
                                <h2 className="text-lg lg:text-xl font-black text-white uppercase tracking-tight mb-1">Carnet de Prescription</h2>
                                <p className="text-[10px] lg:text-sm text-slate-500 font-bold">Rédigez un nouveau bulletin d'ordre de prescription.</p>
                            </div>

                            <div className="space-y-4 bg-slate-900/40 border border-white/5 p-5 rounded-3xl flex flex-col justify-between">
                                <div>
                                    <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest border-b border-white/5 pb-2 mb-4">Carnet de Bulletins de Prescription</h3>
                                    <p className="text-slate-400 text-xs leading-relaxed mb-6">
                                        Rédigez et signez électroniquement des bulletins réglementaires de prescription de sécurité (PN défectueux, ralentissements non listés, etc.).
                                    </p>
                                    
                                    <button
                                        onClick={() => setShowPNForm(true)}
                                        className="w-full py-4 bg-blue-600 hover:bg-blue-500 text-white font-black uppercase tracking-widest rounded-2xl transition-all shadow-lg shadow-blue-900/30 active:scale-95 flex items-center justify-center gap-3 text-xs"
                                    >
                                        Raté de fermeture d'un PN SAL
                                    </button>
                                </div>

                                <div className="mt-6 pt-4 border-t border-white/5">
                                    <h4 className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3">Bulletins signés sur la tablette ({signedBulletins.length})</h4>
                                    {signedBulletins.length === 0 ? (
                                        <p className="text-[10px] text-slate-600 font-bold uppercase tracking-widest italic py-4">Aucun bulletin signé dans l'historique local</p>
                                    ) : (
                                        <div className="space-y-2 max-h-[220px] overflow-y-auto pr-2 custom-scrollbar">
                                            {signedBulletins.map(bull => (
                                                <a
                                                    key={bull.id}
                                                    href={`${API_BASE_URL}/driver_bulletins.php?action=download&id=${bull.id}&token=${localStorage.getItem('nexus_token')}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="w-full bg-[#111827]/40 hover:bg-white/5 border border-white/5 rounded-xl p-3 flex items-center justify-between text-left transition-all group block"
                                                >
                                                    <div className="flex flex-col gap-1">
                                                        <span className="text-white font-bold text-xs uppercase">Bulletin PN N°{bull.sequence_number} (Reçu: {bull.received_number})</span>
                                                        <span className="text-[9px] text-slate-500 font-bold uppercase">Train: {bull.train_number} • PN: {bull.pn_number} (PK {bull.pk_value}) • {new Date(bull.created_at).toLocaleDateString('fr-FR')} {bull.bulletin_time}</span>
                                                    </div>
                                                    <span className="text-[9px] text-blue-400 font-black uppercase tracking-widest group-hover:text-blue-300">Ouvrir (PDF)</span>
                                                </a>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {showPNForm && (
                                <PNBulletinForm
                                    driverName={driverName}
                                    onClose={() => setShowPNForm(false)}
                                    onSuccess={() => {
                                        setShowPNForm(false);
                                        fetchSignedBulletins();
                                    }}
                                />
                            )}
                        </div>
                    )}

                    {activeTab === 'docs' && (
                        <div className="flex-1 flex flex-col gap-6 animate-in slide-in-from-right-4 duration-500">
                            <div className="flex-1 overflow-y-auto space-y-4 pr-2 custom-scrollbar">
                                <div>
                                    <h2 className="text-lg lg:text-xl font-black text-white uppercase tracking-tight mb-1">Documents Réglementaires de Sécurité</h2>
                                    <p className="text-[10px] lg:text-sm text-slate-500 font-bold">Documentation permanente de sécurité et consignes applicables.</p>
                                </div>
                                {[
                                    { name: "Règlement Général de Sécurité (RGS) - Version Consolidée", date: "01/01/2026", id: "rgs" },
                                    { name: "Consignes Locales de Sécurité (CLS) - Ligne Centrale", date: "15/02/2026", id: "cls_centrale" },
                                    { name: "Consignes Locales de Sécurité (CLS) - Ligne de la Balagne", date: "15/02/2026", id: "cls_balagne" }
                                ].map(doc => (
                                    <button
                                        key={doc.id}
                                        onClick={() => alert("Ce document permanent est consultable en version physique dans votre cabine ou auprès du chef de gare.")}
                                        className="w-full group bg-[#111827] hover:bg-blue-500/10 border border-white/5 hover:border-blue-500/30 rounded-2xl lg:rounded-3xl p-4 lg:p-6 transition-all flex items-center justify-between text-left"
                                    >
                                        <div className="flex items-center gap-4">
                                            <div className="p-3 lg:p-4 bg-blue-500/10 rounded-xl lg:rounded-2xl group-hover:bg-blue-500/20 transition-all">
                                                <FileText className="text-blue-500 w-6 h-6 lg:w-8 lg:h-8" />
                                            </div>
                                            <div>
                                                <p className="text-lg font-black text-white uppercase tracking-tight">{doc.name}</p>
                                                <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mt-1">Dernière révision : {doc.date}</p>
                                            </div>
                                        </div>
                                        <ChevronRight className="text-slate-700 group-hover:text-blue-500 transition-all" size={32} />
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {activeTab === 'planning' && (
                        <div className="flex-1 flex flex-col bg-[#111827] rounded-2xl lg:rounded-[3rem] border border-white/5 p-6 lg:p-12 animate-in slide-in-from-right-4 duration-500 overflow-y-auto">
                            <div className="mb-8">
                                <h2 className="text-xl lg:text-2xl font-black text-white uppercase italic mb-2 flex items-center gap-3">
                                    <ClipboardList className="text-blue-500" /> Vos Plannings de Service
                                </h2>
                                <p className="text-slate-500 font-bold text-xs lg:text-sm">Téléchargez le planning officiel au format PDF correspondant à votre zone d'affectation.</p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {['Bastia', 'Ajaccio', 'Calvi'].map(zone => {
                                    // Vérifier si le conducteur est autorisé pour cette zone
                                    const isAuthorized = allowedZones.toLowerCase().includes(zone.toLowerCase());

                                    return (
                                        <div key={zone} className={`p-6 rounded-2xl border flex flex-col justify-between ${
                                            isAuthorized 
                                                ? 'bg-slate-800/40 border-white/5 hover:border-blue-500/30' 
                                                : 'bg-slate-900/20 border-white/5 opacity-50'
                                        }`}>
                                            <div>
                                                <div className="flex items-center justify-between mb-4">
                                                    <span className="text-white font-bold text-base">{zone}</span>
                                                    <span className={`text-[9px] px-2 py-0.5 rounded uppercase font-bold tracking-widest ${
                                                        isAuthorized 
                                                            ? 'bg-green-500/10 text-green-400 border border-green-500/20' 
                                                            : 'bg-red-500/10 text-red-400 border border-red-500/20'
                                                    }`}>
                                                        {isAuthorized ? 'Accès Autorisé' : 'Accès Refusé'}
                                                    </span>
                                                </div>
                                                <p className="text-xs text-slate-500 mb-6">
                                                    {isAuthorized 
                                                        ? `Planning opérationnel complet de la zone ${zone}.` 
                                                        : `Vous n'avez pas l'autorisation d'accéder au planning de la zone ${zone}. Veuillez contacter l'administrateur.`
                                                    }
                                                </p>
                                            </div>

                                            {isAuthorized ? (
                                                <a
                                                    href={`${API_BASE_URL}/download.php?file=planning_${zone.toLowerCase()}&token=${localStorage.getItem('nexus_token')}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs text-center py-3.5 rounded-xl transition-all active:scale-95 block uppercase tracking-widest shadow-lg shadow-blue-900/30"
                                                >
                                                    Consulter (PDF)
                                                </a>
                                            ) : (
                                                <button
                                                    disabled
                                                    className="w-full bg-slate-800 text-slate-600 font-bold text-xs py-3.5 rounded-xl cursor-not-allowed uppercase tracking-widest border border-white/5"
                                                >
                                                    Verrouillé
                                                </button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}
